import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Navbar from "../navbar/Navbar";
import SideBar from "../sidebar/SideBar";
import { RootState } from "../../redux/store";
import { Pagination } from "../shared";
import {
  setPage,
  setTotalItems,
  setTotalPages,
} from "../../redux/reducer/pagination/paginationSlice";
import {
  setUserRoles,
  setAgentTypes,
  setCountryCode,
  setOrderType,
  setReportTypeStatic,
  setParentUserId,
  setThirdPartyApiDropDown,
  setFinalMileIntegration,
  setStateList,
  setAuditListModule,
  setVersion,
} from "../../redux/reducer/staticData/staticDataSlice";
import { GET_PROFILE_DATA, STATIC_DATA_API } from "../../apis/end-points";
import { getApi } from "../../apis/api";

import "../../assets/scss/pages/dashboard.scss";
import { ProfileResponse } from "../../pages/profile/Profile-types";
import { GENERAL_ERROR_MESSAGE } from "../../utils/message";
import { setRole } from "../../redux/reducer/auth/authSlice";
import { setUserInfo } from "../../redux/reducer/navbar/navbarSlice";

// Type for the updated pathsWithPagination structure
type PathWithTabs = {
  path: string | RegExp;
  activeTabs: string[];
};

const Layout: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { activeTab } = useSelector((state: RootState) => state.activeTab);
  const { countryCode } = useSelector((state: RootState) => state.staticData);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any>();
  const { activeTabInnerChildTab } = useSelector(
    (state: RootState) => state.activeTab
  );

  // Add state to track LTL edit mode
  const [isViewingLTLDetails, setIsViewingLTLDetails] = useState<boolean>(
    sessionStorage.getItem("ltlIntegrationEdit") === "true"
  );

  // Add useEffect to watch for sessionStorage changes (per-tab edit-mode flag)
  useEffect(() => {
    const checkLTLEditMode = () => {
      const ltlEditValue = sessionStorage.getItem("ltlIntegrationEdit") === "true";
      setIsViewingLTLDetails(ltlEditValue);
    };

    // Check immediately
    checkLTLEditMode();

    // Set up an interval to check for changes (since localStorage doesn't trigger events)
    const interval = setInterval(checkLTLEditMode, 100);

    return () => clearInterval(interval);
  }, []);

  const pathsWithPagination: PathWithTabs[] = [
    {
      path: "/users",
      activeTabs: [],
    },
    {
      path: "/customers-doc",
      activeTabs: [],
    },
    {
      path: "/customer-users",
      activeTabs: [],
    },
    {
      path: "/customer-items",
      activeTabs: [],
    },
    {
      path: "/customers",
      activeTabs: [],
    },
    {
      path: /^\/customers\/customers(\/[^/]+)?$/,
      activeTabs: [],
    },
    {
      path: "/agent-list",
      activeTabs: [],
    },
    {
      path: "/agent-rates",
      activeTabs: [],
    },
    {
      path: "/agent-hubs",
      activeTabs: [],
    },
    {
      path: "/agent-doc-list",
      activeTabs: [],
    },
    {
      path: "/agent-mile-zone",
      activeTabs: [],
    },
    {
      path: "/agent-mile-zone/add",
      activeTabs: [],
    },
    {
      path: /^\/agent-mile-zone\/edit\/[a-zA-Z0-9!@#$%^&*()-_=+[{\]}\\|;:'",.<>/?]+$/,
      activeTabs: [],
    },
    {
      path: "/agent-weight-zone",
      activeTabs: [],
    },
    {
      path: "/agent-weight-zone/add",
      activeTabs: [],
    },
    {
      path: /^\/agent-weight-zone\/edit\/[a-zA-Z0-9!@#$%^&*()-_=+[{\]}\\|;:'",.<>/?]+$/,
      activeTabs: [],
    },
    {
      path: "/fsc-agent-list",
      activeTabs: [],
    },
    {
      path: "/fsc-agent-list/add",
      activeTabs: [],
    },
    {
      path: /^\/fsc-agent-list\/edit\/[a-zA-Z0-9!@#$%^&*()-_=+[{\]}\\|;:'",.<>/?]+$/,
      activeTabs: [],
    },
    {
      path: "/all-orders",
      activeTabs: [],
    },
    {
      path: "/all-orders",
      activeTabs: [],
    },
    {
      path: "/finalized-order",
      activeTabs: [],
    },
    {
      path: "/cancelled-order",
      activeTabs: [],
    },
    {
      path: "/active-order",
      activeTabs: [],
    },
    {
      path: "/delivered-order",
      activeTabs: [],
    },
    {
      path: "/invoiced-order",
      activeTabs: [],
    },
    {
      path: "/dispatch-board-list",
      activeTabs: [],
    },
    {
      path: /^\/agent-list(\/[^/]+)?$/,
      activeTabs: [
        "agentHubs",
        "agentRateData",
        "agentMileZone",
        "agentWeightZone",
        "fscAgentList",
        "agentRates",
        "agentAdditionalCharges",
      ],
    },
    {
      path: /^\/master-agent-list\/[^/]+$/,
      activeTabs: [
        "agentHubs",
        "agentMileZone",
        "agentWeightZone",
        "fscAgentList",
        "agentRates",
        "agentAdditionalCharges",
      ],
    },
    {
      path: /^\/customer\/rate(\/[^/]+)?$/,
      activeTabs: [
        "fscCustomerList",
        "customerAdditionalCharges",
        "customerRate",
        "ltlIntegration",
        "customerTechFee",
      ],
      // activeTabs: [
      //   "operationalInformation",
      //   "customerRate",
      //   "customerMarkup",
      //   "fscCustomerList",
      //   "customerAdditionalCharges",
      //   "customerInvoiceGroup",
      //   "agentHub",
      //   "ltlIntegration",
      //   "finalMileIntegration"
      // ],
    },
    {
      path: "/billed-order-history",
      activeTabs: [],
    },
    {
      path: "/audit-log",
      activeTabs: [],
    },
    {
      path: "/Xcelarator-api",
      activeTabs: [],
    },
    {
      path: "/agent-api",
      activeTabs: [],
    },
    {
      path: /^\/Xcelarator-api\/agent(\/[^/]+)?$/,
      activeTabs: ["services", "vehicle", "packageType", "apiOrderStatus"],
    },
    {
      path: "/customer-markup",
      activeTabs: [],
    },
    {
      path: "/group-invoice-list",
      activeTabs: [],
    },
    {
      path: "/invoice-list",
      activeTabs: [],
    },
    {
      path: "/finalized-orders",
      activeTabs: [],
    },
    {
      path: "/settings-additional-charges",
      activeTabs: [],
    },
    // {
    //   path: "/create-report",
    //   activeTabs: [],
    // },
    {
      path: "/saved-reports",
      activeTabs: [],
    },
    {
      path: "/system-configuration/package-type",
      activeTabs: [],
    },
    {
      path: "/system-configuration/services",
      activeTabs: [],
    },
      {
      path: "/system-configuration/onward-service",
      activeTabs: [],
    },
      {
      path: "/system-configuration/order-status",
      activeTabs: [],
    },
    {
      path: "/saved-quotes",
      activeTabs: [],
    },
    {
      path: "/system-configuration/email-template",
      activeTabs: [],
    },
    {
      path: "/bulk-order-listing",
      activeTabs: [],
    },
    {
      path: "/bulk-quote-listing",
      activeTabs: [],
    },
    {
      path: "/customer-address-book",
      activeTabs: [],
    },
    {
      path:"/system-configuration/region",
      activeTabs:[],
    },
    {
      path: "/system-configuration/fsc-location",
      activeTabs: [],
    },
    {
      path: "/system-configuration/zip-code-list",
      activeTabs: [],
    },
    {
      path: "/agent-request",
      activeTabs: [],
    },
    {
      path: "/rep-list",
      activeTabs: [],
    },
    {
      path: "/communications/unassigned",
      activeTabs: [],
    },
    {
      path: "/communications/assigned-to-me",
      activeTabs: [],
    },
    {
      path: "/communications/exceptions",
      activeTabs: [],
    },
    {
      path: "/communications/exceptions/resolved",
      activeTabs: [],
    },
    {
      path: "/communications/assigned-to-me/resolved",
      activeTabs: [],
    },
    {
      // A viewed group's Open and Resolved tabs (KAN-1362)
      path: /^\/communications\/groups\/[^/]+(\/resolved)?$/,
      activeTabs: [],
    },
    {
      // The POD Requests and Spot Quotes listings (sprint63 Spot Quotes listing Decisions D3, D5); not their
      // details, nor the Claude Agents settings
      path: "/communications/pod-requests",
      activeTabs: [],
    },
    {
      path: "/communications/spot-quotes",
      activeTabs: [],
    },

  ];

  /**
   * Determine if pagination should be rendered based on path and active tab
   */
  const shouldRenderPagination = pathsWithPagination.some(
    ({ path, activeTabs }) => {
      const isPathMatch =
        typeof path === "string"
          ? location.pathname === path
          : path instanceof RegExp
            ? path.test(location.pathname)
            : false;
      
      // const isPaginationTab =
      //   (isPathMatch && activeTabs.length === 0) ||
      //   (activeTab && activeTabs.length > 0 && activeTabs.includes(activeTab));
      const isPaginationTab =
        isPathMatch &&
        (activeTabs.length === 0 ||
          (activeTab && activeTabs.length > 0 && activeTabs.includes(activeTab)));

      const shouldHidePaginationTabs = ["Customer Rates"];

      return (
        isPaginationTab &&
        !shouldHidePaginationTabs.includes(activeTabInnerChildTab ?? "") &&
        !isViewingLTLDetails  // Hide pagination when viewing LTL details
      );
    }
  );
  // const shouldRenderPagination = pathsWithPagination.some(
  //       ({ path, activeTabs }) => {
  //         const isPathMatch =
  //           typeof path === "string"
  //             ? location.pathname === path
  //             : path instanceof RegExp
  //             ? path.test(location.pathname)
  //             : false;

  //         const isPaginationTab =
  //           (isPathMatch && activeTabs.length === 0) ||
  //           (activeTab && activeTabs.length > 0 && activeTabs.includes(activeTab));

  //         // ✅ hide pagination when AgentRateData (step 3) is active
  //         const shouldHidePaginationTabs = ["Agent Rates", "Customer Rate"]; // or add more as needed

  //         return isPaginationTab && !shouldHidePaginationTabs.includes(activeTab ?? "");
  //       }
  //     );

  /**
   * Determines the appropriate class name for the page based on the current route and pagination status.
   *
   * @param {string} pathname - The current path (route) of the application.
   * @param {boolean} shouldRenderPagination - A boolean indicating whether pagination should be rendered.
   *
   * @returns {string} - The class name that should be applied to the page container.
   *    Returns:
   *    - "page__fullBody" if pagination should be rendered.
   *    - "page__full" if pagination should not be rendered.
   *    - An empty string if the route is in the list of exceptions where 'page__full' should not be applied.
   */
  const getPageClassName = (
    pathname: string,
    shouldRenderPagination: boolean
  ): string => {
    // List of routes that should not have the 'page__full' class
    const noFullClassRoutes: string[] = [];

    // Check if the current path is in the list
    if (noFullClassRoutes.includes(pathname)) {
      return ""; // Apply a different class or no class
    }

    // Apply 'page__full' if pagination should not be rendered, otherwise 'page__fullBody'
    return shouldRenderPagination ? "page__fullBody" : "page__full";
  };

  const getStaticData = async () => {
    try {
      const apiResponse = await getApi<any>(STATIC_DATA_API);
      if (apiResponse.success) {
        dispatch(setUserRoles(apiResponse.data?.role));
        dispatch(setAgentTypes(apiResponse.data?.agentType));
        dispatch(setCountryCode(apiResponse.data?.countryCodeList));
        dispatch(setOrderType(apiResponse.data?.orderType));
        dispatch(setReportTypeStatic(apiResponse.data?.reportType));
        dispatch(setParentUserId(apiResponse.data?.parentUserId));
        dispatch(setThirdPartyApiDropDown(apiResponse.data?.thirdPartyApiName));
        dispatch(setFinalMileIntegration(apiResponse.data?.finalMileIntegration));
        dispatch(setStateList(apiResponse.data?.state));
        dispatch(setAuditListModule(apiResponse.data?.auditListModule));
        dispatch(setVersion(apiResponse.data?.version));
      }
    } catch (error) {
      console.error("Static data error", error);
    }
  };

  const getProfileData = async () => {
    try {
      setLoading(true);
      let userId = localStorage.getItem("userId") ?? "";
      const url = `${GET_PROFILE_DATA}${userId}`;
      const apiResponse = await getApi<ProfileResponse>(url);
      if (apiResponse.success) {
        let profileData: any = apiResponse.data;
        setData(profileData);
        dispatch(setRole(profileData?.role?.name));
        dispatch(
          setUserInfo({
            username: `${profileData?.firstName || ""} ${profileData?.lastName || ""
              }`,
            userId: profileData.id,
            userEmail: profileData.email,
            userRole: profileData?.role?.name || "",
            userProfile: `${profileData.firstName?.[0] || ""}${profileData.lastName?.[0] || ""
              }`,
          })
        );
      }
    } catch (error) {
      console.log(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };

  // Reset pagination data when the current location changes
  useEffect(() => {
    dispatch(setTotalItems(0));
    dispatch(setTotalPages(1));
    dispatch(setPage(1));
    !countryCode.length && getStaticData();
  }, [location.pathname]);

  useEffect(() => {
    getProfileData();
  }, []);

  return (
    <>
      <section className="layoutContainer">
        <SideBar />
        <div className="layoutGrid">
          <div
            className={`page ${getPageClassName(
              location.pathname,
              shouldRenderPagination
            )}`}
          >
            <Navbar />
            <div className="page__body">
              {/* <HeaderFilters /> */}
              <Outlet />
            </div>
            {shouldRenderPagination && <Pagination />}
          </div>
        </div>
      </section>
    </>
  );
};

export default Layout;
